import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Plus, X } from 'lucide-react';
import { getExerciseInputMode } from '../../../lib/exerciseDisplay';

/**
 * Inline set editor — the sets/reps grid rendered directly under an exercise card
 * (no dialog). Lifted from the old SetEditorModal so behaviour + testids are identical.
 *
 * Props:
 *   exercise      — exercise object with `sets` (required)
 *   language      — current language
 *   setTypeColors — map of set_type -> tailwind classes
 *   onAddSet, onRemoveSet(setIdx), onChangeSetType(setIdx, val), onChangeField(setIdx, field, val)
 */
export default function SetEditorInline({
  exercise, setTypeColors = {},
  onAddSet, onRemoveSet, onChangeSetType, onChangeField,
}) {
  const { t } = useTranslation();
  if (!exercise) return null;
  const isCardio = getExerciseInputMode(exercise) === 'cardio';
  // minmax(0,1fr) lets the type <select> shrink instead of forcing horizontal overflow;
  // fixed columns are sized to fit the ~260px routine-board column with no h-scroll.
  const gridCols = 'grid-cols-[22px_minmax(0,1fr)_38px_38px_38px_34px]';

  return (
    <div className="bg-muted/30 rounded-xl p-3" data-testid="set-editor-inline">
      {/* Column headers */}
      <div className={`grid ${gridCols} gap-1 mb-1.5`}>
        <span className="text-xs text-muted-foreground font-semibold uppercase text-center tracking-wider">{t('routineEditor.setHeader')}</span>
        <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">{t('routineEditor.typeHeader')}</span>
        {isCardio ? (
          <span className="col-span-3 text-xs text-muted-foreground font-semibold uppercase tracking-wider">{t('workout.colMinutes')}</span>
        ) : (
          <>
            <span className="text-xs text-muted-foreground font-semibold uppercase text-center tracking-wider">{t('routineEditor.minHeader')}</span>
            <span className="text-xs text-muted-foreground font-semibold uppercase text-center tracking-wider">{t('routineEditor.maxHeader')}</span>
            <span className="text-xs text-muted-foreground font-semibold uppercase text-center tracking-wider">{t('routineEditor.kgHeader')}</span>
          </>
        )}
        <span></span>
      </div>
      {/* Sets */}
      <div className="space-y-1">
        {exercise.sets?.map((set, setIdx) => (
          <div key={setIdx} className={`grid ${gridCols} gap-1 items-center`} data-testid={`set-row-${setIdx}`}>
            <div className="text-center text-xs font-bold text-muted-foreground">{setIdx + 1}</div>
            <select value={set.set_type}
              onChange={(e) => onChangeSetType(setIdx, e.target.value)}
              className={`min-w-0 w-full h-9 md:h-7 rounded-lg border text-xs font-semibold px-1.5 bg-transparent cursor-pointer ${setTypeColors[set.set_type] || setTypeColors.work || ''}`}
              data-testid={`set-type-select-${setIdx}`}>
              <option value="warmup">{t('routineEditor.warmup')}</option>
              <option value="work">{t('routineEditor.workType')}</option>
              <option value="drop">{t('routineEditor.drop')}</option>
              <option value="backoff">{t('routineEditor.backoff')}</option>
            </select>
            {isCardio ? (
              <Input type="number" inputMode="numeric" min="0"
                value={set.target_duration_seconds ? Math.round(set.target_duration_seconds / 60) : ''}
                onChange={(e) => onChangeField(setIdx, 'target_duration_seconds', e.target.value === '' ? null : (parseInt(e.target.value, 10) || 0) * 60)}
                placeholder={t('workout.colMinutes')}
                aria-label={t('workout.targetMin')}
                className="col-span-3 h-7 text-xs text-center px-1 rounded-lg" data-testid={`set-target-minutes-${setIdx}`} />
            ) : (
              <>
                <Input type="number" value={set.target_reps_min}
                  onChange={(e) => onChangeField(setIdx, 'target_reps_min', parseInt(e.target.value) || 0)}
                  className="h-7 text-xs text-center px-1 rounded-lg" data-testid={`set-min-reps-${setIdx}`} />
                <Input type="number" value={set.target_reps_max}
                  onChange={(e) => onChangeField(setIdx, 'target_reps_max', parseInt(e.target.value) || 0)}
                  className="h-7 text-xs text-center px-1 rounded-lg" data-testid={`set-max-reps-${setIdx}`} />
                <Input type="number" value={set.target_weight || set.weight || ''}
                  onChange={(e) => onChangeField(setIdx, 'target_weight', parseFloat(e.target.value) || 0)}
                  className="h-7 text-xs text-center px-1 rounded-lg" placeholder="0" data-testid={`set-weight-${setIdx}`} />
              </>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8 md:h-7 md:w-7 justify-self-center text-muted-foreground hover:text-destructive"
              tooltip={t('tooltips.removeSet')}
              onClick={() => onRemoveSet(setIdx)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
      <button className="w-full mt-2 h-11 md:h-7 rounded-lg border border-dashed border-border/60 text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors flex items-center justify-center gap-1"
        onClick={onAddSet} data-testid="add-set-btn">
        <Plus className="h-3 w-3" /> {t('routineEditor.addSet')}
      </button>
    </div>
  );
}
